<template lang="pug">
.plan(:class="{ active }")
  .plan__deco(aria-hidden="true")

  .plan__selected-badge(v-if="active" aria-hidden="true")
    svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor")
      path(fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd")

  .plan__header
    svg.plan__wifi-icon(xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true")
      path(stroke-linecap="round" stroke-linejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z")
    h1.plan__name {{ nombre_comercial }}
    img.plan__logo(:src="require('@/assets/logo.png')" alt="GigNet")

  .plan__pricing
    span.plan__price {{ numberFormatter(renta_mensual_con_iva) }}
    span.plan__currency MXN
    span.plan__period {{ $t('wizard_contract.plans.period') }}

  .plan__speed
    p.plan__speed-label {{ $t('wizard_contract.plans.prefix') }}
    .plan__speed-num {{ velocidad_incluida }}
    .plan__speed-unit Mbps

  p.plan__description(v-if="texto_tarifa_descripcion") {{ texto_tarifa_descripcion }}

  .plan__install-badge
    svg.plan__install-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true")
      path(fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd")
    span(v-if="!sin_costo_instalacion") {{ $t('wizard_contract.plans.install') }}: {{ instalacion_con_iva }} MXN
    span(v-else) {{ $t('wizard_contract.plans.install') }}: {{ $t('wizard_contract.plans.install_free') }}
</template>

<script>
import { numberFormatter } from "@/utils/formatters";

export default {
  props: {
    is_promotion: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    id: {
      type: Number,
      default: 0,
    },
    plazo_minimo_meses: {
      type: Number,
      default: 0,
    },
    nombre_comercial: {
      type: String,
      default: "",
    },
    velocidad_incluida: {
      type: Number,
      default: 0,
    },
    speed_promotion: {
      type: Number,
      default: 0,
    },
    instalacion_sin_iva: {
      type: Number,
      default: 0,
    },
    instalacion_con_iva: {
      type: Number,
      default: 0,
    },
    renta_mensual_con_iva: {
      type: Number,
      default: 0,
    },
    texto_tarifa_descripcion: {
      type: [String, Boolean],
      default: "",
    },
    plan_servicio_internet: {
      type: Array,
      default: () => [0, ""],
    },
    sin_costo_instalacion: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { numberFormatter };
  },
};
</script>

<style lang="scss" scoped>
// Palette taken from gignetmexico.com ("Elige el plan a tu medida")
$gignet-navy: #00224e;
$gignet-teal: #34c0cb;
$gignet-blue: #0d66aa;
$gignet-cyan: #00f2fe;

.plan {
  @apply select-none relative overflow-hidden cursor-pointer;
  @apply flex flex-col items-center justify-evenly text-center text-white;
  font-family: "Poppins", sans-serif;
  border-radius: 15px;
  padding: 20px 14px;
  background-image: linear-gradient(180deg, $gignet-teal 0%, $gignet-blue 100%);
  box-shadow: 0 4px 18px rgba(13, 102, 170, 0.25);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  &.active {
    transform: scale(1.05);
    box-shadow: 0 10px 30px rgba(0, 34, 78, 0.35), 0 0 0 3px $gignet-navy;
  }
}

// Dotted texture overlay, like the site's dots-deco background
.plan__deco {
  @apply absolute inset-0 pointer-events-none;
  opacity: 0.19;
  background-image: radial-gradient(
    rgba(255, 255, 255, 0.9) 1.5px,
    transparent 1.5px
  );
  background-size: 18px 18px;
}

.plan__selected-badge {
  @apply absolute flex items-center justify-center rounded-full;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  color: $gignet-blue;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 34, 78, 0.3);

  svg {
    width: 18px;
    height: 18px;
  }
}

.plan__header {
  @apply relative flex flex-col items-center w-full;
  gap: 4px;
}

.plan__wifi-icon {
  display: block;
  width: 32px;
  height: 32px;
  opacity: 0.9;
}

.plan__name {
  max-width: 222px;
  margin: 2px 0;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
}

.plan__logo {
  width: 80px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.plan__pricing {
  @apply relative flex flex-wrap items-baseline justify-center;
  gap: 0 4px;
  margin: 10px 0 6px;
}

.plan__price {
  font-size: 2.6rem;
  font-weight: 800;
  line-height: 1;
}

.plan__currency,
.plan__period {
  font-size: 0.9rem;
}

.plan__period {
  @apply w-full;
  opacity: 0.85;
}

.plan__speed {
  @apply relative;
  margin: 6px 0;
  line-height: 0.9;
}

.plan__speed-label {
  @apply uppercase;
  margin-bottom: 4px;
  font-size: 13px;
  letter-spacing: 0.1em;
}

.plan__speed-num {
  font-size: 60px;
  font-weight: 900;
  color: $gignet-cyan;
}

.plan__speed-unit {
  font-size: 24px;
  font-weight: 700;
}

.plan__description {
  @apply relative;
  margin: 6px 0;
  font-size: 13px;
}

.plan__install-badge {
  @apply relative flex items-center rounded-md;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: $gignet-navy;
  transition: background-color 0.3s;

  .plan:not(.active):hover & {
    background-color: darken($gignet-navy, 4%);
  }

  .active & {
    background-color: white;
    color: $gignet-navy;
  }
}

.plan__install-icon {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}
</style>
